/**
 * Touch detection utility
 * Detects if the current device supports touch input
 */

/**
 * Detects if the device has ANY touch capability
 * 
 * Returns true for:
 * - Smartphones and tablets
 * - Laptops with touchscreens (e.g., Surface Pro, modern Windows laptops)
 * - Desktop monitors with touch support
 * 
 * Use this when you need to handle touch interactions regardless of
 * whether touch is the primary or secondary input method.
 * 
 * @returns {boolean} True if device supports touch in any form
 */
export function isTouchDevice(): boolean {
  // Check multiple indicators for maximum compatibility
  return (
    // Standard touch events support
    ('ontouchstart' in window) ||
    
    // Touch points on navigator (modern browsers)
    (navigator.maxTouchPoints > 0) ||
    
    // MS Touch points (older IE/Edge)
    ('msMaxTouchPoints' in navigator && (navigator as unknown as { msMaxTouchPoints: number }).msMaxTouchPoints > 0) ||
    
    // Media query for coarse pointer (touch primary)
    (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) ||
    
    // Additional check for devices that don't support hover
    (window.matchMedia && window.matchMedia('(hover: none)').matches)
  );
}

/**
 * Detects if touch is the PRIMARY input method
 * 
 * Returns true for:
 * - Smartphones
 * - Tablets
 * 
 * Returns false for:
 * - Laptops with touchscreens (mouse/trackpad is primary)
 * - Desktop monitors with touch (mouse is primary)
 * 
 * Use this when you want touch-specific behavior only on devices
 * where users primarily interact via touch, not on devices that
 * happen to have touch as a secondary input.
 * 
 * @returns {boolean} True only if touch is the primary input method
 */
export function isPrimaryTouchDevice(): boolean {
  // Device has touch AND doesn't have fine pointer (mouse) as primary
  // The (pointer: coarse) media query indicates the primary pointing device
  // is imprecise (like a finger), not precise (like a mouse)
  return isTouchDevice() && 
    window.matchMedia && 
    window.matchMedia('(pointer: coarse)').matches;
}

/**
 * Initialize touch detection and add class to document
 * Useful for CSS styling based on touch capability
 */
export function initializeTouchDetection(): void {
  if (isTouchDevice()) {
    document.documentElement.classList.add('touch-device');
  } else {
    document.documentElement.classList.add('no-touch-device');
  }
  
  // Also add primary touch class if applicable
  if (isPrimaryTouchDevice()) {
    document.documentElement.classList.add('primary-touch-device');
  }
}

// Auto-initialize on module load
if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeTouchDetection);
  } else {
    initializeTouchDetection();
  }
}